import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Video, ExternalLink } from 'lucide-react';
import { api } from '../../lib/api';
import type { Meeting } from '../../types';
import { formatDateTime } from '../../lib/format';
import { useUpload } from '../../lib/useUpload';
import {
  Spinner, EmptyState, Modal, Alert, StatusBadge, ConfirmDialog, UploadProgress,
  inputClass, labelClass, primaryBtn, secondaryBtn,
} from '../../components/ui';

interface MeetingForm {
  title: string;
  description: string;
  meeting_date: string;
  duration: number;
}

const emptyForm: MeetingForm = { title: '', description: '', meeting_date: '', duration: 30 };

export default function Meetings() {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<MeetingForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [recordingFor, setRecordingFor] = useState<Meeting | null>(null);
  const [recordingLink, setRecordingLink] = useState('');
  const [recordingFile, setRecordingFile] = useState<File | null>(null);
  const recordingUpload = useUpload();

  const load = () => {
    setLoading(true);
    api
      .get<Meeting[]>('/meetings')
      .then(setMeetings)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/meetings', form);
      setModalOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/meetings/${id}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  const openRecording = (m: Meeting) => {
    setRecordingLink(m.recording_url || '');
    setRecordingFile(null);
    setError('');
    setRecordingFor(m);
  };

  const handleSaveRecording = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recordingFor) return;
    recordingUpload.reset();
    setError('');
    try {
      if (recordingFile) {
        const fd = new FormData();
        fd.append('recording', recordingFile);
        await recordingUpload.run(`/meetings/${recordingFor.id}/recording`, fd, 'PUT');
      } else {
        // An empty link is a deliberate "remove the recording", so it is sent
        // as an empty string rather than treated as nothing to do.
        await api.put(`/meetings/${recordingFor.id}/recording`, { recording_url: recordingLink || '' });
      }
      setRecordingFor(null);
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const upcoming = meetings.filter((m) => m.status === 'scheduled');
  const past = meetings.filter((m) => m.status !== 'scheduled');

  const renderList = (list: Meeting[]) =>
    list.length === 0 ? (
      <EmptyState message="No meetings." />
    ) : (
      <div className="grid gap-4">
        {list.map((m) => (
          <div key={m.id} className="bg-white p-5 rounded-lg shadow-lg">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center space-x-3 mb-1">
                  <h3 className="font-semibold">{m.title}</h3>
                  <StatusBadge status={m.status} />
                </div>
                {m.description && <p className="text-sm text-gray-500 mb-1">{m.description}</p>}
                <p className="text-xs text-gray-500">{formatDateTime(m.meeting_date)} · {m.duration} min · by {m.created_by_name || '—'}</p>
                {m.recording_url && (
                  <a
                    href={m.recording_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center mt-2 px-3 py-1.5 rounded-lg border border-green-200 bg-green-50 text-green-700 text-xs hover:bg-green-100"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> View Recording
                  </a>
                )}
              </div>
              <div className="flex items-center space-x-2 shrink-0">
                <button
                  onClick={() => openRecording(m)}
                  className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs hover:bg-gray-50"
                >
                  <Video className="h-3.5 w-3.5 mr-1.5" /> Recording
                </button>
                <a
                  href={m.meeting_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-3 py-1.5 rounded-lg bg-primary text-black text-xs hover:bg-primary-dark"
                >
                  <Video className="h-3.5 w-3.5 mr-1.5" /> Join
                </a>
                <button
                  onClick={() => setDeleteId(m.id)}
                  className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="text-2xl font-bold">Meetings</h1>
        <button onClick={() => { setError(''); setModalOpen(true); }} className={primaryBtn}>
          <Plus className="h-4 w-4" /> Schedule Meeting
        </button>
      </div>

      {loading ? (
        <Spinner label="Loading meetings..." />
      ) : (
        <div className="space-y-10">
          <div>
            <h2 className="text-lg font-semibold mb-4">Upcoming</h2>
            {renderList(upcoming)}
          </div>
          <div>
            <h2 className="text-lg font-semibold mb-4">Past / Other</h2>
            {renderList(past)}
          </div>
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Schedule Meeting">
        {error && <Alert>{error}</Alert>}
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className={labelClass}>Title *</label>
            <input className={inputClass} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea className={inputClass} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Date & Time *</label>
              <input
                type="datetime-local"
                className={inputClass}
                required
                value={form.meeting_date}
                onChange={(e) => setForm({ ...form, meeting_date: e.target.value })}
              />
            </div>
            <div>
              <label className={labelClass}>Duration (minutes)</label>
              <input
                type="number"
                min={5}
                step={5}
                className={inputClass}
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: Number(e.target.value) })}
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            A Jitsi meeting room will be created automatically. All users will be notified.
          </p>
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setModalOpen(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} className={primaryBtn}>{saving ? 'Scheduling...' : 'Schedule'}</button>
          </div>
        </form>
      </Modal>

      <Modal open={!!recordingFor} onClose={() => setRecordingFor(null)} title={`Recording — ${recordingFor?.title || ''}`}>
        {error && <Alert>{error}</Alert>}
        <form onSubmit={handleSaveRecording} className="space-y-4">
          <div>
            <label className={labelClass}>Recording link (YouTube, Drive, Jitsi, etc.)</label>
            <input
              className={inputClass}
              value={recordingLink}
              onChange={(e) => { setRecordingLink(e.target.value); setRecordingFile(null); }}
              placeholder="https://..."
            />
          </div>
          <div className="text-center text-xs text-gray-400">— or upload a file —</div>
          <div>
            <label className={labelClass}>Recording file (video, audio, PDF)</label>
            <input
              type="file"
              accept="video/*,audio/*,.pdf"
              className={inputClass}
              onChange={(e) => { setRecordingFile(e.target.files?.[0] || null); setRecordingLink(''); }}
            />
          </div>
          <p className="text-xs text-gray-500">Leave both empty and save to remove the recording.</p>
          <UploadProgress
            percent={recordingUpload.percent}
            phase={recordingUpload.phase}
            error={recordingUpload.error}
            onCancel={recordingUpload.cancel}
          />
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setRecordingFor(null)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={recordingUpload.busy} className={primaryBtn}>
              {recordingUpload.phase === 'uploading'
                ? `Uploading ${recordingUpload.percent}%`
                : recordingUpload.phase === 'processing' ? 'Saving...' : 'Save Recording'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDelete(deleteId)}
        title="Delete Meeting"
        message="Are you sure you want to delete this meeting?"
      />
    </div>
  );
}
